import {
  CircularProgress, Grid,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import {
  selectContext, selectContextError, selectContextStatus, exitSelectionMode,
} from '../redux/features/context/contextSlice';
import { shadowBleed } from '../components/common/cardShadow';
import { HeaderSpacer } from '../components/Header';
import {
  fetchSites, deleteSite, updateSite, shareSite, mergeSites,
} from '../redux/features/sites/sitesSlice';
import {
  approveSite, rejectSite, batchModerateSites,
} from '../redux/features/pending/pendingSlice';
import StyledBackdrop from '../components/StyledBackdrop';
import ViewError from '../components/common/ViewError';
import useLoadErrorHandler from '../hooks/useLoadErrorHandler';
import SiteCardGrid from '../components/SiteCardGrid';
import FilterBar from '../components/FilterBar';
import SelectionBar, { SelectionBarVerdictButton } from '../components/SelectionBar';
import { RejectReasonMenu } from '../components/common/VerdictBar';
import Login from '../components/Login';
import { DELETE_LABEL, UNSHARE_LABEL } from '../common/ActionLabels';
import {
  getSiteTitle, isSameSiteDomain, mergeSite, mustSignIn, shareResultMessage,
} from '../common/ItemTools';
import { setSnackbar } from '../redux/features/snackbarSlice';
import { batchVerdictMessage, verdictPastTense, verdictResultMessage } from '../common/VerdictMessages';
import useGridLayout from '../hooks/useGridLayout';
import useConfirm from '../hooks/useConfirm';
import useBatchSelection from '../hooks/useBatchSelection';
import usePaging from '../hooks/usePaging';

function SitesView(props) {
  const type = 'sites';
  const { namespace } = props;
  const { userInfo } = useSelector((state) => state.users);

  const { pageSize } = useGridLayout();

  const [searchParams] = useSearchParams();
  const status = useSelector(selectContextStatus);
  const loadError = useSelector(selectContextError);
  const onLoadError = useLoadErrorHandler('sites');
  const currContext = useSelector(selectContext);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { confirm, confirmDialog } = useConfirm();

  const page = +searchParams.get('page');
  const filter = searchParams.get('filter');
  const sortby = searchParams.get('sortby');
  const source = searchParams.get('source');
  const view = searchParams.get('view');

  const forceSignIn = mustSignIn(namespace, page, userInfo);

  const context = (!forceSignIn
    && currContext
    && currContext.namespace === namespace
    && currContext.type === type) ? currContext : null;

  // Extract all URL parameters - single source of truth
  const urlParams = useMemo(() => ({
    page: page >= 1 ? page : 1,
    filter,
    sortby,
    source,
    view,
  }), [page, filter, sortby, source, view]);

  // Fetch sites based on current URL params
  const fetchCurrentSites = useCallback(() => {
    if (forceSignIn) return;

    const pageNumber = urlParams.page;
    const offset = (pageNumber - 1) * pageSize;

    const params = {
      pageNumber,
      offset,
      limit: pageSize,
      ...(urlParams.filter && { filter: urlParams.filter }),
      ...(urlParams.sortby && { sortby: urlParams.sortby }),
      ...(urlParams.source && { source: urlParams.source }),
      ...(urlParams.view && { view: urlParams.view }),
    };

    dispatch(fetchSites({ namespace, params }))
      .unwrap()
      .catch(onLoadError);
  }, [dispatch, namespace, urlParams, forceSignIn, pageSize, onLoadError]);

  // Single effect: URL changes → fetch data
  useEffect(() => {
    if (forceSignIn) return;
    fetchCurrentSites();
  }, [
    urlParams.page,
    urlParams.filter,
    urlParams.sortby,
    urlParams.source,
    urlParams.view,
    namespace,
    userInfo,
    forceSignIn,
    fetchCurrentSites,
  ]);

  const isModerating = namespace === 'pending';
  const showDelete = Boolean(userInfo) && !isModerating;
  const showUnshare = Boolean(userInfo?.isModerator) && !isModerating;
  const showMerge = namespace === 'private'
    ? Boolean(userInfo)
    : Boolean(userInfo?.isModerator);
  const [bulkRejectAnchor, setBulkRejectAnchor] = useState(null);

  const pageSites = useMemo(
    () => (context?.sites || []).map(mergeSite).filter(Boolean),
    [context],
  );
  const {
    selectionMode, selectedIds, selectedCount,
    pageIds, selectAllLabel, selectPage, selectAll, clear: clearSelection,
  } = useBatchSelection(pageSites);

  const onSiteRead = (site) => {
    navigate(isModerating ? `/moderate/sites/${site.id}` : `/sites/${site.id}`);
  };
  const onSiteDelete = async (site) => {
    const ok = await confirm({
      title: 'Delete site?',
      message: `"${getSiteTitle(site) || 'This site'}" will be permanently removed from your library.`,
      confirmLabel: DELETE_LABEL,
      destructive: true,
    });
    if (!ok) return;
    dispatch(deleteSite({ namespace: 'private', siteId: site.id }))
      .unwrap()
      .then(() => {
        // Refresh current view after deleting
        fetchCurrentSites();
      })
      .catch(() => {
        dispatch(setSnackbar({
          snackbarOpen: true,
          snackbarType: 'error',
          snackbarMessage: 'Failed to delete site.',
        }));
      });
  };
  const onSiteUnshare = async (site) => {
    const ok = await confirm({
      title: 'Unshare site?',
      message: `"${getSiteTitle(site) || 'This site'}" will be removed from the public record. Creators keep their private copies.`,
      confirmLabel: UNSHARE_LABEL,
      destructive: true,
    });
    if (!ok) return;
    dispatch(deleteSite({ namespace: 'public', siteId: site.id }))
      .unwrap()
      .then(() => {
        fetchCurrentSites();
      })
      .catch(() => {
        dispatch(setSnackbar({
          snackbarOpen: true,
          snackbarType: 'error',
          snackbarMessage: 'Failed to unshare site.',
        }));
      });
  };
  const onSiteShare = (site) => {
    dispatch(shareSite({ siteId: site.id }))
      .unwrap()
      .then((result) => {
        dispatch(setSnackbar({
          snackbarOpen: true,
          snackbarType: 'success',
          snackbarMessage: shareResultMessage(result),
        }));
        fetchCurrentSites();
      })
      .catch(() => {
        dispatch(setSnackbar({
          snackbarOpen: true,
          snackbarType: 'error',
          snackbarMessage: 'Failed to share the site.',
        }));
      });
  };

  const onSiteVerdict = (site, kind, reason) => {
    const thunk = kind === 'approve'
      ? approveSite(site)
      : rejectSite({ id: site.id, reason });
    dispatch(thunk)
      .unwrap()
      .then(() => {
        dispatch(setSnackbar({
          snackbarOpen: true,
          snackbarType: 'success',
          snackbarMessage: verdictResultMessage(kind, 'site', getSiteTitle(site), reason),
        }));
        fetchCurrentSites();
      })
      .catch(() => {
        dispatch(setSnackbar({
          snackbarOpen: true,
          snackbarType: 'error',
          snackbarMessage: `Failed to ${kind} site.`,
        }));
        fetchCurrentSites();
      });
  };

  const onBatchVerdict = async (kind, reason = null) => {
    if (selectedCount === 0) return;
    try {
      const { failed } = await dispatch(
        batchModerateSites({ ids: selectedIds, verdict: kind, reason }),
      ).unwrap();
      if (failed.length > 0) {
        dispatch(setSnackbar({
          snackbarOpen: true,
          snackbarType: 'warning',
          snackbarMessage: `${failed.length} of ${selectedIds.length} sites could not be ${verdictPastTense(kind)}.`,
        }));
      } else {
        dispatch(setSnackbar({
          snackbarOpen: true,
          snackbarType: 'success',
          snackbarMessage: batchVerdictMessage(kind, 'site', selectedIds.length, reason),
        }));
      }
    } catch (error) {
      dispatch(setSnackbar({
        snackbarOpen: true,
        snackbarType: 'error',
        snackbarMessage: `Failed to ${kind} sites.`,
      }));
    } finally {
      dispatch(exitSelectionMode());
      fetchCurrentSites();
    }
  };

  const onSiteEdit = (site) => {
    navigate(isModerating
      ? `/moderate/sites/${site.id}?edit`
      : `/edit/site/${site.id}`);
  };

  const mergeMessage = (target, sources) => {
    const targetName = getSiteTitle(target) || 'this site';
    return sources.length === 1
      ? `"${getSiteTitle(sources[0]) || 'This site'}" will be merged into "${targetName}" and removed.`
      : `${sources.length} sites will be merged into "${targetName}" and removed.`;
  };

  const onSiteMerge = async (targetSiteId, sourceSiteIds) => {
    const target = pageSites.find((site) => site.id === targetSiteId);
    const sources = sourceSiteIds
      .map((sourceId) => pageSites.find((site) => site.id === sourceId))
      .filter(Boolean);
    if (!target || !sources.length) return;

    if (!sources.every((site) => isSameSiteDomain(site.domain, target.domain))) {
      dispatch(setSnackbar({
        snackbarOpen: true,
        snackbarType: 'warning',
        snackbarMessage: 'Only sites on the same domain can be merged.',
      }));
      return;
    }

    const ok = await confirm({
      title: sources.length === 1 ? 'Merge site?' : 'Merge sites?',
      message: mergeMessage(target, sources),
      confirmLabel: 'Merge',
      destructive: true,
    });
    if (!ok) return;

    dispatch(mergeSites({ namespace, targetSiteId, sourceSiteIds }))
      .unwrap()
      .then(() => {
        fetchCurrentSites();
      })
      .catch(() => {
        dispatch(setSnackbar({
          snackbarOpen: true,
          snackbarType: 'error',
          snackbarMessage: 'Failed to merge sites.',
        }));
      });
  };

  const onSiteFavorite = (site) => {
    const patch = { isFavorite: !site.isFavorite };
    dispatch(updateSite({ namespace: 'private', siteId: site.id, patch }))
      .unwrap()
      .then(() => {
        // Refresh current view after marking as favorite
        fetchCurrentSites();
      })
      .catch(() => {
        dispatch(setSnackbar({
          snackbarOpen: true,
          snackbarType: 'error',
          snackbarMessage: 'Failed to favorite site.',
        }));
      });
  };

  const { handleNextPage, handlePreviousPage } = usePaging(urlParams.page);

  return (
    <>
      <HeaderSpacer />
      { !forceSignIn && <FilterBar entity="sites" /> }
      {confirmDialog}
      { forceSignIn && <Login />}
      { !forceSignIn && (
        <StyledBackdrop open={status === 'loading' || (!context && status !== 'failed')}>
          <CircularProgress color="inherit" />
        </StyledBackdrop>
      )}
      { !forceSignIn && !context && status === 'failed' && (
        <ViewError message={loadError} entity="sites" />
      )}
      { !forceSignIn && context && status !== 'loading' && (
        <Grid container sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', ...shadowBleed }}>
          <Grid container item sx={{ flex: 1, ...shadowBleed }}>
            <Grid xs={12} item sx={{ height: '100%' }}>
              <SiteCardGrid
                currentPage={context}
                isReadOnly={false}
                listActions={{
                  handlePreviousPage,
                  handleNextPage,
                }}
                siteActions={{
                  ...(isModerating && { onSiteVerdict }),
                  ...(showDelete && { onSiteDelete }),
                  ...(showUnshare && { onSiteUnshare }),
                  onSiteRead,
                  onSiteEdit,
                  ...(!isModerating && Boolean(userInfo) && { onSiteFavorite }),
                  ...(!isModerating && { onSiteShare }),
                  ...(showMerge && { onSiteMerge }),
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      )}
      { isModerating && selectionMode && (
        <SelectionBar
          total={pageIds.length}
          selectAllLabel={selectAllLabel}
          onSelectPage={selectPage}
          onSelectAll={selectAll}
          onClear={clearSelection}
          actions={(
            <>
              <SelectionBarVerdictButton kind="approve" onClick={() => onBatchVerdict('approve')}>
                <CheckIcon sx={{ fontSize: 17 }} />
                Approve
              </SelectionBarVerdictButton>
              <SelectionBarVerdictButton
                kind="reject"
                onClick={(event) => setBulkRejectAnchor(event.currentTarget)}
              >
                <CloseIcon sx={{ fontSize: 17 }} />
                Reject
              </SelectionBarVerdictButton>
              <RejectReasonMenu
                anchorEl={bulkRejectAnchor}
                title={`Reject ${selectedCount} as`}
                onClose={() => setBulkRejectAnchor(null)}
                onPick={(reason) => onBatchVerdict('reject', reason)}
              />
            </>
          )}
        />
      )}
    </>
  );
}

export default SitesView;
